import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../store";
import { fetchChatbots } from "../../features/ai_chatbots/aiChatbotsSlice";
import { Button, Spinner, Nav, Tab, Tabs, Badge, Alert } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { FaArrowLeft, FaExclamationTriangle, FaCheck } from "react-icons/fa";
import { TbEdit } from "react-icons/tb";
import { RiChatVoiceAiLine, RiBrainLine, RiPaletteLine, RiDatabaseLine, RiSettings4Line } from "react-icons/ri";
import { Icon } from "../../components/common/Icon";
import { useAIChatbotForm } from "./components/form/hooks/useAIChatbotForm";
import { startEditTour, startOnboardingWizardTour } from "../../utils/chatbotTour";
import { FaPlay } from "react-icons/fa";
// Tab Components
import GeneralTab from "./components/form/tabs/GeneralTab";
import AIConfigTab from "./components/form/tabs/AIConfigTab";
import TrainingTab from "./components/form/tabs/TrainingTab";
import DatabaseTab from "./components/form/tabs/DatabaseTab";
import WidgetTab from "./components/form/tabs/WidgetTab";
import FlowsTab from "./components/form/tabs/FlowsTab";
import ModulesTab from "./components/form/tabs/ModulesTab";
import AIChatbotWidgetModal from "./components/AIChatbotWidgetModal";

// Styles
import "./components/form/styles/AIChatbotForm.css";

const AIChatbotsPageEdit: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch<AppDispatch>();
    const { chatbots, isLoading } = useSelector((s: RootState) => s.aiChatbots);
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [showWidgetModal, setShowWidgetModal] = useState(false);

    const chatbotId = id ? parseInt(id) : null;
    const isEditMode = !!chatbotId;
    const chatbot = chatbotId ? chatbots.find(c => c.id === chatbotId) : undefined;
    const defaultCompanyId = (location.state as any)?.companyId;

    // Determine current wizard step from URL or default
    const wizardStep = searchParams.get("step") || "general";

    // In strict Wizard Mode (creation flow), we force the active tab based on query param
    const isNew = !isEditMode;
    const isWizardMode = isNew || !!searchParams.get("step");
    const [activeTab, setActiveTab] = useState(wizardStep);

    // Sync activeTab with wizardStep when it changes, but only if we are in wizard flow (new or explicitly stepped)
    useEffect(() => {
        if (wizardStep) {
            setActiveTab(wizardStep);
        }
    }, [wizardStep]);

    useEffect(() => {
        // If editing and chatbot is not in the store, fetch all chatbots
        // Only if we have an ID
        if (chatbotId && !chatbot && !isLoading) {
            dispatch(fetchChatbots({}));
        }
    }, [chatbotId, chatbot, isLoading, dispatch]);

    // Auto-start wizard tour if onboarding just finished
    useEffect(() => {
        if (isWizardMode && searchParams.get("onboarding") === "true") {
            const tourKey = `wizard_tour_seen_${wizardStep}_${id}`;
            const shown = localStorage.getItem(tourKey);
            if (!shown) {
                // Short timeout to ensure DOM is ready
                const timer = setTimeout(() => {
                    startOnboardingWizardTour(t, wizardStep);
                    localStorage.setItem(tourKey, 'true');
                }, 1000);
                return () => clearTimeout(timer);
            }
        }
    }, [isWizardMode, wizardStep, searchParams, id, t]);

    const handleSuccess = (result?: any) => {
        // Wizard Flow Logic - ONLY if we started as New or have an explicit Step param
        if (isWizardMode) {
            if (!isEditMode) {
                // Step 1 Completed (General) -> Move to Training
                if (result && result.id) {
                    navigate(`/ai-chatbots/${result.id}/edit?step=training`, { replace: true });
                } else {
                    navigate("/ai-chatbots");
                }
            } else {
                // Continuation of wizard
                if (wizardStep === "general") {
                    navigate(`/ai-chatbots/${id}/edit?step=training`, { replace: true });
                } else if (wizardStep === "training") {
                    navigate(`/ai-chatbots/${id}/edit?step=widget-settings`, { replace: true });
                } else {
                    // Final step (widget-settings) -> Show Modal instead of navigating
                    setShowWidgetModal(true);
                }
            }
        } else {
            // Normal Edit Save - No stepped Wizard
            navigate("/ai-chatbots");
            dispatch(fetchChatbots({}));
        }
    };

    const handleCancel = () => {
        navigate("/ai-chatbots");
    };

    const {
        formData,
        setFormData,
        settingsData,
        setSettingsData,
        isSubmitting,
        validationErrors,
        companiesOptions,
        handleInputChange,
        handleSubmit,
        isCompanyOwner,
        isMarketingSpecialist,
    } = useAIChatbotForm({
        chatbot,
        onSuccess: handleSuccess,
        // We pass a dummy onHide because we handle navigation in onSuccess
        onHide: () => { },
        defaultModule: "ai_tools",
        defaultCompanyId: defaultCompanyId
    });

    if (isLoading && isEditMode && !chatbot) {
        return (
            <div className="chatbots-page">
                <div className="loading-container">
                    <Spinner animation="border" className="loading-spinner" />
                </div>
            </div>
        );
    }

    if (isEditMode && !chatbot && !isLoading) {
        return (
            <div className="chatbots-page">
                <div className="page-header-card">
                    <div className="page-header-text">
                        <h1>{t("Chatbot not found")}</h1>
                        <Button onClick={() => navigate("/ai-chatbots")}>
                            <FaArrowLeft /> {t("back")}
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    // Define tabs for the Wizard
    const wizardSteps = ["general", "training", "widget-settings"];
    const isWizardFlow = isNew || wizardSteps.includes(wizardStep); // Simple check

    // Only show tabs relevant to the current wizard step if in creation/wizard flow
    // User requested: "General -> Training -> Widget". Others hidden.
    // If regular Edit (no step param?), show all.
    // If Step param exists, create focused view? 
    // Flow for Create New AI Agent; for Edit, keep the same.

    // So:
    // If isNew = true OR (isEditMode and we have a ?step=... param implies we are continuing the wizard)
    // Actually, to differentiate "Wizard Edit" vs "Normal Edit", we rely on the `step` param.
    // Normal edit navigation doesn't pass `step`.



    let displayedTabs = [
        { key: "general", label: t("general"), icon: RiChatVoiceAiLine },
        { key: "training", label: t("tab_training"), icon: RiBrainLine, disabled: isNew },
        { key: "widget-settings", label: t("tab_widget_design"), icon: RiPaletteLine, disabled: isNew },
        { key: "flows", label: t("tab_flows"), icon: RiChatVoiceAiLine, disabled: isNew },
        { key: "database-config", label: t("tab_integrations"), icon: RiDatabaseLine, disabled: true, comingSoon: true },
        { key: "modules", label: "Modules", icon: RiSettings4Line, disabled: isNew },
        { key: "ai-config", label: t("tab_advanced_settings"), icon: RiSettings4Line, disabled: isNew },
    ];

    if (isWizardMode) {
        // Determine completion based on current step
        // Only consider "done" if we are past it OR if we just finished onboarding
        const isGeneralDone = wizardStep !== 'general' || searchParams.get("onboarding") === "true";
        const isTrainingDone = wizardStep === 'widget-settings';

        // Helper for icon color/type
        const getIcon = (stepKey: string, defaultIcon: any, isDone: boolean) => {
            if (isDone) return { icon: FaCheck, className: 'text-success' };
            if (activeTab === stepKey) return { icon: TbEdit, className: 'text-warning' }; // Current editing is orange
            return { icon: defaultIcon, className: 'text-dark' }; // Future/Pending is black
        };

        const genStatus = getIcon('general', RiChatVoiceAiLine, isGeneralDone);
        const trainStatus = getIcon('training', RiBrainLine, isTrainingDone);
        const widgetStatus = getIcon('widget-settings', RiPaletteLine, false);

        displayedTabs = [
            {
                key: "general",
                label: "1. " + t("general"),
                icon: genStatus.icon,
                iconClass: genStatus.className,
                disabled: false // Always clickable if we are in wizard mode (unless creating new?)
            },
            {
                key: "training",
                label: `2. ${t("tab_training")}`,
                icon: trainStatus.icon,
                iconClass: trainStatus.className,
                disabled: !isGeneralDone // clickable if general is done
            },
            {
                key: "widget-settings",
                label: `3. ${t("tab_widget_design")}`,
                icon: widgetStatus.icon,
                iconClass: widgetStatus.className,
                disabled: !isTrainingDone // clickable if training is done
            },
        ] as any[];
    }

    // Determine Button Text based on step
    let nextButtonText = t("Save Changes");
    if (isWizardMode) {
        if (wizardStep === "general") nextButtonText = isNew ? t("Create & Next") : t("Save & Next");
        else if (wizardStep === "training") nextButtonText = t("Save & Next");
        else if (wizardStep === "widget-settings") nextButtonText = t("Finish");
    }

    return (
        <div className="chatbots-page">
            <div className="page-header-card chatbots-edit-header">
                <div className="page-header-text">
                    <h1>{isEditMode ? chatbot?.name : t("create_new_chatbot")}</h1>
                    <p>{isEditMode ? t("manage_your_ai_powered_chatbot_assistants") : t("Configure your new assistant")}</p>
                </div>
                <div className="page-header-right">
                    {/* Tour only in standard edit mode */}
                    {(isEditMode && !isWizardMode) && (
                        <Button
                            className="chatbots-actions-tour me-2"
                            onClick={() => startEditTour(t, setActiveTab as any)}
                        >
                            <FaPlay className="me-1" /> {t("tour.start")}
                        </Button>
                    )}
                    {isWizardMode && (
                        <Button
                            className="chatbots-actions-tour me-2"
                            onClick={() => startOnboardingWizardTour(t, wizardStep)}
                        >
                            <FaPlay className="me-1" /> {t("tour.start")}
                        </Button>
                    )}
                    <Button
                        onClick={handleCancel}
                        className="chatbots-actions-cancel"
                        disabled={isSubmitting}
                    >
                        {t("Cancel")}
                    </Button>
                    <Button
                        className="chatbots-actions-save"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <>
                                <span className="" role="status" aria-hidden="true"></span>
                                {t("Saving")}
                            </>
                        ) : (
                            nextButtonText
                        )}
                    </Button>
                </div>
            </div>
            <div className="chatbots-edit-content">
                <Tab.Container
                    activeKey={activeTab}
                    // Allow navigation if the step is enabled in our displayedTabs logic
                    onSelect={(k) => {
                        if (k) {
                            // Update URL step param to match clicked tab, so the wizard logic stays consistent
                            if (isWizardMode) {
                                setSearchParams({ step: k }, { replace: true });
                            } else {
                                setActiveTab(k);
                            }
                        }
                    }}
                >
                    <div className="chatbots-tabs-container">
                        <Nav variant="pills" className="">
                            {displayedTabs.map((tab: any) => (
                                <Nav.Item key={tab.key} className="chatbots-tabs-item">
                                    <Nav.Link
                                        eventKey={tab.key}
                                        className="chatbots-tabs-link"
                                        disabled={tab.disabled}
                                    >
                                        <Icon icon={tab.icon} className={`nav-icon ${(tab as any).iconClass || ''}`} />
                                        <span className={`nav-label`}>{tab.label}</span>
                                        {tab.comingSoon && (
                                            <Badge bg="soft-warning" text="dark" className="coming-soon-badge">
                                                {t("soon")}
                                            </Badge>
                                        )}
                                    </Nav.Link>
                                </Nav.Item>
                            ))}
                        </Nav>
                    </div>
                </Tab.Container>
                <div className={`chatbots-edit-body ${activeTab === "widget-settings" ? "p-0 d-flex flex-column" : ""}`}>
                    {validationErrors.length > 0 && (
                        <Alert variant="danger" className="modern-alert">
                            <div className="d-flex align-items-center">
                                <Icon icon={FaExclamationTriangle} className="me-2" />
                                <strong>Please check the following:</strong>
                            </div>
                            <ul className="mb-0 mt-2 ps-3">
                                {validationErrors.map((error, index) => (
                                    <li key={index}>{error}</li>
                                ))}
                            </ul>
                        </Alert>
                    )}

                    <Tab.Container activeKey={activeTab}>
                        <Tab.Content>
                            <Tab.Pane eventKey="general">
                                <GeneralTab
                                    formData={formData}
                                    handleInputChange={handleInputChange}
                                    setFormData={setFormData}
                                    companiesOptions={companiesOptions}
                                    isCompanyOwner={!!isCompanyOwner}
                                    chatbot={chatbot}
                                />
                            </Tab.Pane>

                            {/* Render other tabs only if not new, OR if we are in that step? 
                                Actually, render them but they are hidden by CSS via keys.
                                We just need to ensure data access is safe.
                                TrainingTab needs chatbot prop. If isNew, chatbot is undefined.
                                But TrainingTab is only accessible after step 1 saved (so isEditMode=true).
                            */}

                            <Tab.Pane eventKey="ai-config">
                                <AIConfigTab
                                    formData={formData}
                                    handleInputChange={handleInputChange}
                                    settingsData={settingsData}
                                    setSettingsData={setSettingsData}
                                    setFormData={setFormData}
                                    isMarketingSpecialist={!!isMarketingSpecialist}
                                    chatbot={chatbot}
                                />
                            </Tab.Pane>

                            <Tab.Pane eventKey="training">
                                <TrainingTab
                                    chatbot={chatbot}
                                    settingsData={settingsData}
                                    setSettingsData={setSettingsData}
                                    isEditMode={isEditMode}
                                />
                            </Tab.Pane>

                            <Tab.Pane eventKey="flows">
                                <FlowsTab
                                    formData={formData}
                                    setFormData={setFormData}
                                    isEditMode={isEditMode}
                                />
                            </Tab.Pane>

                            <Tab.Pane eventKey="database-config">
                                <DatabaseTab
                                    formData={formData}
                                    handleInputChange={handleInputChange}
                                />
                            </Tab.Pane>

                            <Tab.Pane eventKey="modules">
                                <ModulesTab chatbotId={chatbotId} />
                            </Tab.Pane>

                            <Tab.Pane eventKey="widget-settings">
                                <WidgetTab formData={formData} setFormData={setFormData} />
                            </Tab.Pane>
                        </Tab.Content>
                    </Tab.Container>
                </div>
            </div>

            <AIChatbotWidgetModal
                show={showWidgetModal}
                onHide={() => {
                    setShowWidgetModal(false);
                    navigate("/ai-chatbots");
                }}
                chatbotId={chatbotId || (chatbot?.id) || null}
            />
        </div>
    );
};

export default AIChatbotsPageEdit;
